import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import type { PmsType, RentManagerConfig, StructureBilling } from '@/Structures/types';
import { PMS_PROVIDERS } from '@/Structures/types';
import type { Unit as StructureUnit } from '@/Units/types';
import { formatNumber, naturalCompare } from '@/util';

import {
  detectMidCycleChanges,
  downloadRentManagerCsv,
  buildRentManagerChargePayloads,
} from '../pmsExport';
import { usePmsApiEnabled } from '@/hooks/usePmsApiEnabled';
import { listRmOccupants, uploadChargesToRm } from '@/Structures/View/components/pmsApi';
import type { RmChargePayload, RmOccupant } from '@/Structures/View/components/pmsApi';
import { RmMatchingDialog } from '@/Structures/View/components/RmMatchingDialog';

interface PmsActionsProps {
  bill: StructureBilling;
  localInvoices: any[];
  tz: string;
  structureName: string;
  eventDetails: Record<string, any>;
  trackEvent: (event: string, properties?: Record<string, any>) => void;
  pmsType?: PmsType | null;
  pmsConfig?: RentManagerConfig | null;
  unitPmsNames?: Record<string, string>;
  unitPmsIds?: Record<string, number>;
  onPmsSettingsUpdate: (
    pmsType: PmsType | null,
    pmsConfig: RentManagerConfig | null,
  ) => Promise<void>;
  structureUnits?: StructureUnit[];
  onStructureRefresh?: () => void;
}

/**
 * Compute unit_cost (fee portion) for each unit.
 * Same logic as mergeInvoicesToUnits in BreakdownDialog but returns UnitBilling[].
 */
function enrichBillUnits(bill: StructureBilling) {
  const lineItems = bill.line_items ?? [];
  const splitItems = lineItems.filter((item) => item.type === 'SPLIT');
  const flatItems = lineItems.filter((item) => item.type === 'FLAT');
  const unitFlatItems = lineItems.filter((item) => item.type === 'UNIT_FLAT');
  const billableUnitsCount = bill.billable_units_count ?? bill.units.length;
  const commonAreaCostPerUnit = (bill.common_area_usage ?? []).reduce(
    (sum, ca) => sum + ca.split_cost,
    0,
  );

  return bill.units.map((unit) => {
    let unitCost = 0;
    const isBillable = unit.is_billable ?? true;

    if (isBillable) {
      unitCost += commonAreaCostPerUnit;
      splitItems.forEach((item) => {
        unitCost += (item.cost ?? 0) / billableUnitsCount;
      });
      flatItems.forEach((item) => {
        unitCost += item.cost ?? 0;
      });
    }

    unitFlatItems.forEach((item) => {
      if (item.unit_id === unit.id) {
        unitCost += item.cost ?? 0;
      }
    });

    return { ...unit, unit_cost: unitCost };
  });
}

/** Build itemized fee breakdown for a unit (mirrors buildFeeBreakdown in BreakdownDialog). */
function buildChargeBreakdown(bill: StructureBilling, unitId: string, isBillable: boolean) {
  const breakdown: { label: string; amount: number }[] = [];
  const unit = bill.units.find((u) => u.id === unitId);
  breakdown.push({ label: 'Water Usage', amount: unit?.unit_cost_meter ?? 0 });

  if (isBillable) {
    const commonAreaUsage = bill.common_area_usage ?? [];
    commonAreaUsage.forEach((ca) => {
      breakdown.push({ label: `Common area: ${ca.name}`, amount: ca.split_cost });
    });

    const lineItems = bill.line_items ?? [];
    const billableUnitsCount = bill.billable_units_count ?? bill.units.length;

    lineItems
      .filter((item) => item.type === 'SPLIT')
      .forEach((fee) => {
        breakdown.push({
          label: fee.description || 'Split Fee',
          amount: (fee.cost ?? 0) / billableUnitsCount,
        });
      });

    lineItems
      .filter((item) => item.type === 'FLAT')
      .forEach((fee) => {
        breakdown.push({ label: fee.description || 'Flat Fee', amount: fee.cost ?? 0 });
      });
  }

  const unitFlatFees = (bill.line_items ?? []).filter(
    (item) => item.type === 'UNIT_FLAT' && item.unit_id === unitId,
  );
  unitFlatFees.forEach((fee) => {
    breakdown.push({ label: fee.description || 'Unit-Specific Fee', amount: fee.cost ?? 0 });
  });

  return breakdown;
}

export const PmsActions = ({
  bill,
  localInvoices,
  tz,
  structureName,
  eventDetails,
  trackEvent,
  pmsType,
  pmsConfig,
  unitPmsNames,
  unitPmsIds,
  onPmsSettingsUpdate,
  structureUnits,
  onStructureRefresh,
}: PmsActionsProps): JSX.Element => {
  const enrichedUnits = useMemo(() => enrichBillUnits(bill), [bill]);

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  // Dialog state
  const [showPmsPickerDialog, setShowPmsPickerDialog] = useState(false);
  const [pickerPmsType, setPickerPmsType] = useState<PmsType | ''>('');
  const [showRmMatchingForSetup, setShowRmMatchingForSetup] = useState(false);
  const [showRmMatchingForUpload, setShowRmMatchingForUpload] = useState(false);
  const [showPmsSetupDialog, setShowPmsSetupDialog] = useState(false);
  const [showMidCycleWarning, setShowMidCycleWarning] = useState(false);
  const [midCycleWarnings, setMidCycleWarnings] = useState<
    { unitName: string; invoiceCount: number }[]
  >([]);
  const [pmsSetupPropertyName, setPmsSetupPropertyName] = useState('');
  const [pmsSetupChargeType, setPmsSetupChargeType] = useState('');
  const [pmsSetupUnitMappings, setPmsSetupUnitMappings] = useState<Record<string, string>>({});
  const [pmsSetupSaving, setPmsSetupSaving] = useState(false);

  // Upload confirmation dialog state
  const [showUploadConfirm, setShowUploadConfirm] = useState(false);
  const [uploadPayloads, setUploadPayloads] = useState<RmChargePayload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [occupants, setOccupants] = useState<Record<string, RmOccupant>>({});
  const [loadingOccupants, setLoadingOccupants] = useState(false);

  // Flag: after saving mappings via "Upload Charges..." flow, auto-open confirmation
  // when the parent re-renders with updated pmsConfig/unitPmsIds
  const pendingUploadAfterSave = useRef(false);

  // When pmsConfig/unitPmsIds update after saving mappings, auto-open upload confirmation
  useEffect(() => {
    if (
      pendingUploadAfterSave.current &&
      pmsConfig?.property_id &&
      pmsConfig?.charge_type_id &&
      unitPmsIds &&
      Object.keys(unitPmsIds).length > 0
    ) {
      pendingUploadAfterSave.current = false;
      const payloads = buildRentManagerChargePayloads(
        bill,
        enrichedUnits,
        pmsConfig,
        tz,
        unitPmsIds,
      );
      if (payloads.length > 0) {
        setUploadPayloads(payloads);
        setShowUploadConfirm(true);
        setLoadingOccupants(true);
        listRmOccupants(payloads.map((p) => p.UnitID))
          .then((result) => setOccupants(result))
          .catch(() => setOccupants({}))
          .finally(() => setLoadingOccupants(false));
      } else {
        toast.error('No billable units with RM unit mappings to upload', { closeButton: true });
      }
    }
  }, [pmsConfig, unitPmsIds, bill, enrichedUnits, tz]);

  // Feature flag: PMS API integration
  const { pmsApiEnabled } = usePmsApiEnabled();

  // Capabilities (API features gated by feature flag)
  const provider = pmsType ? PMS_PROVIDERS[pmsType] : null;
  const capabilities = provider?.capabilities ?? [];
  const hasCsvExport = capabilities.includes('csv_export');
  const hasApiUpload = pmsApiEnabled && capabilities.includes('api_upload');
  const isConfigured = !!pmsType && !!pmsConfig;
  const isApiReady = !!pmsConfig?.property_id && !!pmsConfig?.charge_type_id;

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // --- PMS Export Handlers ---

  const performPmsExport = useCallback(
    async (config: RentManagerConfig, exportUnitPmsNames: Record<string, string> = {}) => {
      const warnings = detectMidCycleChanges(enrichedUnits, localInvoices);
      if (warnings.length > 0) {
        setMidCycleWarnings(warnings);
        setShowMidCycleWarning(true);
        return;
      }
      await downloadRentManagerCsv(
        bill,
        enrichedUnits,
        config,
        tz,
        structureName,
        exportUnitPmsNames,
      );
      trackEvent('PMS Export Downloaded', { ...eventDetails, pmsType: 'RENT_MANAGER' });
      toast.success('Rent Manager export downloaded', { closeButton: true });
    },
    [bill, enrichedUnits, localInvoices, tz, structureName, trackEvent, eventDetails],
  );

  const handlePmsExport = useCallback(() => {
    handleMenuClose();
    if (pmsType && pmsConfig) {
      performPmsExport(pmsConfig, unitPmsNames ?? {});
    } else {
      // Open first-export setup dialog (fallback for CSV-only PMS)
      const billableUnits = enrichedUnits.filter((u) => {
        const isBillable = u.is_billable ?? true;
        const flags = u.unit_flags ?? [];
        return isBillable && !flags.includes('COMMON_AREA') && !flags.includes('NON_BILLABLE');
      });
      setPmsSetupPropertyName(structureName);
      setPmsSetupChargeType('');
      const mappings: Record<string, string> = {};
      [...billableUnits]
        .sort((a, b) => naturalCompare(a.name, b.name))
        .forEach((u) => {
          mappings[u.id] = u.name;
        });
      setPmsSetupUnitMappings(mappings);
      setShowPmsSetupDialog(true);
    }
  }, [pmsType, pmsConfig, unitPmsNames, performPmsExport, enrichedUnits, structureName]);

  const prepareUploadConfirmation = useCallback(() => {
    if (!pmsConfig?.property_id || !pmsConfig?.charge_type_id) {
      return false;
    }
    const payloads = buildRentManagerChargePayloads(
      bill,
      enrichedUnits,
      pmsConfig,
      tz,
      unitPmsIds ?? {},
    );
    if (payloads.length === 0) {
      toast.error('No billable units with RM unit mappings to upload', { closeButton: true });
      return false;
    }
    setUploadPayloads(payloads);
    setShowUploadConfirm(true);
    setLoadingOccupants(true);
    listRmOccupants(payloads.map((p) => p.UnitID))
      .then((result) => setOccupants(result))
      .catch(() => setOccupants({}))
      .finally(() => setLoadingOccupants(false));
    return true;
  }, [pmsConfig, unitPmsIds, bill, enrichedUnits, tz]);

  const handleUploadToRm = useCallback(() => {
    handleMenuClose();
    if (!pmsConfig?.property_id || !pmsConfig?.charge_type_id) {
      setShowRmMatchingForUpload(true);
      return;
    }
    prepareUploadConfirmation();
  }, [pmsConfig, prepareUploadConfirmation]);

  const occupiedPayloads = useMemo(
    () => uploadPayloads.filter((p) => occupants[String(p.UnitID)] !== null),
    [uploadPayloads, occupants],
  );

  const vacantCount = uploadPayloads.length - occupiedPayloads.length;

  const handleConfirmUpload = useCallback(async () => {
    setIsUploading(true);
    try {
      await uploadChargesToRm(bill.billing_id, occupiedPayloads);
      trackEvent('PMS Charges Uploaded', {
        ...eventDetails,
        pmsType: 'RENT_MANAGER',
        chargeCount: occupiedPayloads.length,
      });
      toast.success(`Uploaded ${occupiedPayloads.length} charge(s) to Rent Manager`, {
        closeButton: true,
      });
      setShowUploadConfirm(false);
    } catch (err: any) {
      toast.error(`Failed to upload charges: ${err.message}`, { closeButton: true });
    } finally {
      setIsUploading(false);
    }
  }, [bill.billing_id, occupiedPayloads, trackEvent, eventDetails]);

  const handleMidCycleConfirm = useCallback(async () => {
    setShowMidCycleWarning(false);
    if (!pmsConfig) return;
    await downloadRentManagerCsv(
      bill,
      enrichedUnits,
      pmsConfig,
      tz,
      structureName,
      unitPmsNames ?? {},
    );
    trackEvent('PMS Export Downloaded', {
      ...eventDetails,
      pmsType: 'RENT_MANAGER',
      midCycleWarning: true,
    });
    toast.success('Rent Manager export downloaded', { closeButton: true });
  }, [pmsConfig, unitPmsNames, bill, enrichedUnits, tz, structureName, trackEvent, eventDetails]);

  const handlePmsSetupSaveAndExport = useCallback(async () => {
    if (!pmsSetupPropertyName.trim()) {
      toast.error('Property Short Name is required', { closeButton: true });
      return;
    }
    setPmsSetupSaving(true);
    try {
      const config: RentManagerConfig = {
        property_short_name: pmsSetupPropertyName.trim(),
        default_charge_type: pmsSetupChargeType.trim() || undefined,
      };
      await onPmsSettingsUpdate('RENT_MANAGER', config);
      const { updateUnitPmsConfig } = await import('@/Units/api');
      await Promise.all(
        Object.entries(pmsSetupUnitMappings).map(([unitId, pmsName]) =>
          updateUnitPmsConfig(
            unitId,
            pmsName ? 'RENT_MANAGER' : null,
            pmsName ? { unit_name: pmsName } : null,
          ),
        ),
      );
      setShowPmsSetupDialog(false);
      await performPmsExport(config, pmsSetupUnitMappings);
    } catch {
      toast.error('Failed to save PMS settings', { closeButton: true });
    } finally {
      setPmsSetupSaving(false);
    }
  }, [
    pmsSetupPropertyName,
    pmsSetupChargeType,
    pmsSetupUnitMappings,
    onPmsSettingsUpdate,
    performPmsExport,
  ]);

  const handleSetupClick = () => {
    setPickerPmsType('');
    setShowPmsPickerDialog(true);
  };

  const buttonLabel = isConfigured ? `Send to ${provider!.label}` : 'Set Up PMS...';

  return (
    <>
      {isConfigured ? (
        <>
          <SWLoadingButton onClick={handleMenuOpen} endIcon={<ArrowDropDownIcon />}>
            {buttonLabel}
          </SWLoadingButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {hasCsvExport && <MenuItem onClick={handlePmsExport}>Export CSV</MenuItem>}
            {hasApiUpload && isApiReady && (
              <MenuItem onClick={handleUploadToRm}>Upload Charges</MenuItem>
            )}
            {hasApiUpload && !isApiReady && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setShowRmMatchingForUpload(true);
                }}
              >
                Upload Charges...
              </MenuItem>
            )}
          </Menu>
        </>
      ) : (
        <SWLoadingButton onClick={handleSetupClick}>{buttonLabel}</SWLoadingButton>
      )}

      {/* Mid-Cycle Tenant Change Warning Dialog */}
      <Dialog open={showMidCycleWarning} onClose={() => setShowMidCycleWarning(false)}>
        <DialogTitle>
          Mid-Cycle Tenant Changes Detected
          <IconButton
            aria-label="close"
            onClick={() => setShowMidCycleWarning(false)}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The following units had tenant changes during this billing period. The export will
            include one row per unit with the total charge. Rent Manager will assign the charge to
            the current occupant.
          </Typography>
          {midCycleWarnings.map((w) => (
            <Typography key={w.unitName} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
              &bull; {w.unitName} ({w.invoiceCount} invoices)
            </Typography>
          ))}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            You may want to manually adjust charges in Rent Manager after import.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SWLoadingButtonOutlined onClick={() => setShowMidCycleWarning(false)}>
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton onClick={handleMidCycleConfirm}>Export Anyway</SWLoadingButton>
        </DialogActions>
      </Dialog>

      {/* PMS Type Picker Dialog */}
      <Dialog
        open={showPmsPickerDialog}
        onClose={() => setShowPmsPickerDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Set Up PMS Integration
          <IconButton
            aria-label="close"
            onClick={() => setShowPmsPickerDialog(false)}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select your Property Management System to get started.
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={pickerPmsType}
            onChange={(e) => setPickerPmsType(e.target.value as PmsType | '')}
            SelectProps={{ native: true }}
          >
            <option value="">Select a PMS...</option>
            {(Object.keys(PMS_PROVIDERS) as PmsType[]).map((key) => (
              <option key={key} value={key}>
                {PMS_PROVIDERS[key].label}
              </option>
            ))}
          </TextField>
          {pickerPmsType && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {PMS_PROVIDERS[pickerPmsType as PmsType]?.capabilities.includes('api_config') && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setShowPmsPickerDialog(false);
                    setShowRmMatchingForSetup(true);
                  }}
                >
                  Connect to {PMS_PROVIDERS[pickerPmsType as PmsType].label} (Recommended)
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowPmsPickerDialog(false);
                  // Pre-fill manual setup
                  const billableUnits = enrichedUnits.filter((u) => {
                    const isBillable = u.is_billable ?? true;
                    const flags = u.unit_flags ?? [];
                    return (
                      isBillable &&
                      !flags.includes('COMMON_AREA') &&
                      !flags.includes('NON_BILLABLE')
                    );
                  });
                  setPmsSetupPropertyName(structureName);
                  setPmsSetupChargeType('');
                  const mappings: Record<string, string> = {};
                  [...billableUnits]
                    .sort((a, b) => naturalCompare(a.name, b.name))
                    .forEach((u) => {
                      mappings[u.id] = u.name;
                    });
                  setPmsSetupUnitMappings(mappings);
                  setShowPmsSetupDialog(true);
                }}
              >
                Configure Manually
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* First-Export PMS Setup Dialog (manual text form — fallback for CSV-only PMS) */}
      <Dialog
        open={showPmsSetupDialog}
        onClose={() => !pmsSetupSaving && setShowPmsSetupDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Set Up Rent Manager Export
          <IconButton
            onClick={() => setShowPmsSetupDialog(false)}
            disabled={pmsSetupSaving}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Configure your Rent Manager integration to export billing data. These settings will be
            saved for future exports.
          </Typography>

          {pmsApiEnabled && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Manual configuration only supports CSV exports. To upload charges directly to Rent
              Manager, use &quot;Connect to Rent Manager&quot; instead.
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label="Property Short Name"
            value={pmsSetupPropertyName}
            onChange={(e) => setPmsSetupPropertyName(e.target.value)}
            helperText="Must match the Property Short Name in Rent Manager"
            disabled={pmsSetupSaving}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            size="small"
            label="Default Charge Type (optional)"
            value={pmsSetupChargeType}
            onChange={(e) => setPmsSetupChargeType(e.target.value)}
            helperText="Leave blank to set during Rent Manager import"
            disabled={pmsSetupSaving}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Unit Name Mappings
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Map SimpleSub unit names to Rent Manager unit names. Pre-filled with SimpleSub names.
          </Typography>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>SimpleSub Unit Name</TableCell>
                  <TableCell>Rent Manager Unit Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(pmsSetupUnitMappings).map(([unitId, rmName]) => {
                  const unit = bill.units.find((u) => u.id === unitId);
                  return (
                    <TableRow key={unitId}>
                      <TableCell>
                        <Typography variant="body2">{unit?.name || unitId}</Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={rmName}
                          onChange={(e) =>
                            setPmsSetupUnitMappings((prev) => ({
                              ...prev,
                              [unitId]: e.target.value,
                            }))
                          }
                          disabled={pmsSetupSaving}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SWLoadingButtonOutlined
            onClick={() => setShowPmsSetupDialog(false)}
            disabled={pmsSetupSaving}
          >
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton onClick={handlePmsSetupSaveAndExport} loading={pmsSetupSaving}>
            Save &amp; Export
          </SWLoadingButton>
        </DialogActions>
      </Dialog>

      {/* RM Matching Dialog — initial setup */}
      {structureUnits && (
        <RmMatchingDialog
          open={showRmMatchingForSetup}
          onClose={() => setShowRmMatchingForSetup(false)}
          units={structureUnits}
          onUpdate={onPmsSettingsUpdate}
          onRefresh={onStructureRefresh}
          existingConfig={pmsConfig}
        />
      )}

      {/* RM Matching Dialog — upload (requires charge type) */}
      {structureUnits && (
        <RmMatchingDialog
          open={showRmMatchingForUpload}
          onClose={() => setShowRmMatchingForUpload(false)}
          units={structureUnits}
          onUpdate={onPmsSettingsUpdate}
          onRefresh={onStructureRefresh}
          existingConfig={pmsConfig}
          requireChargeType
          onSaveSuccess={() => {
            // Flag so the effect opens the upload confirmation after props update
            pendingUploadAfterSave.current = true;
          }}
        />
      )}
      {/* Upload Charges Confirmation Dialog */}
      <Dialog
        open={showUploadConfirm}
        onClose={() => !isUploading && setShowUploadConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Upload to Rent Manager
          <IconButton
            aria-label="close"
            onClick={() => setShowUploadConfirm(false)}
            disabled={isUploading}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            The following charges will be uploaded to Rent Manager:
          </Typography>
          {vacantCount > 0 && !loadingOccupants && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {vacantCount} unit{vacantCount !== 1 ? 's have' : ' has'} no active tenant in Rent
              Manager and will be skipped.
            </Alert>
          )}
          <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Unit</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...uploadPayloads]
                  .sort((a, b) => {
                    const unitA = enrichedUnits.find((u) => (unitPmsIds ?? {})[u.id] === a.UnitID);
                    const unitB = enrichedUnits.find((u) => (unitPmsIds ?? {})[u.id] === b.UnitID);
                    const nameA = unitPmsNames?.[unitA?.id ?? ''] ?? unitA?.name ?? '';
                    const nameB = unitPmsNames?.[unitB?.id ?? ''] ?? unitB?.name ?? '';
                    return naturalCompare(nameA, nameB);
                  })
                  .map((payload) => {
                    const ssUnit = enrichedUnits.find(
                      (u) => (unitPmsIds ?? {})[u.id] === payload.UnitID,
                    );
                    const rmUnitName =
                      unitPmsNames?.[ssUnit?.id ?? ''] ?? `RM Unit ${payload.UnitID}`;
                    const isBillable = ssUnit?.is_billable ?? true;
                    const breakdown = ssUnit
                      ? buildChargeBreakdown(bill, ssUnit.id, isBillable)
                      : [];
                    const occupant = occupants[String(payload.UnitID)];
                    const isVacant = !loadingOccupants && occupant === null;
                    return (
                      <TableRow key={payload.UnitID} sx={isVacant ? { opacity: 0.5 } : undefined}>
                        <TableCell>
                          <Typography variant="body2">{rmUnitName}</Typography>
                          {ssUnit && ssUnit.name !== rmUnitName && (
                            <Typography variant="caption" color="textSecondary">
                              {ssUnit.name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {loadingOccupants ? (
                            <Typography variant="body2" color="textSecondary">
                              Loading...
                            </Typography>
                          ) : isVacant ? (
                            <Typography variant="body2" color="error">
                              No active tenant
                            </Typography>
                          ) : occupant ? (
                            <Typography variant="body2">{occupant.Name}</Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              breakdown.length > 0 ? (
                                <Box>
                                  {breakdown.map((item, idx) => (
                                    <Box
                                      key={idx}
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        minWidth: 150,
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ mr: 2 }}>
                                        {item.label}:
                                      </Typography>
                                      <Typography variant="caption">
                                        ${formatNumber(item.amount, 2)}
                                      </Typography>
                                    </Box>
                                  ))}
                                  <Box
                                    sx={{
                                      borderTop: 1,
                                      borderColor: 'divider',
                                      mt: 0.5,
                                      pt: 0.5,
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ fontWeight: 'bold', mr: 2 }}
                                    >
                                      Total:
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                      ${formatNumber(payload.Amount, 2)}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                ''
                              )
                            }
                            arrow
                            placement="left"
                          >
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                cursor: 'help',
                                borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                                '&:hover': { borderBottomColor: 'primary.main' },
                              }}
                            >
                              ${formatNumber(payload.Amount, 2)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {/* Total row inside the table body to avoid double border */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ fontWeight: 600, borderBottom: 'none' }}>
                    Total ({occupiedPayloads.length} unit
                    {occupiedPayloads.length !== 1 ? 's' : ''})
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>
                    $
                    {formatNumber(
                      occupiedPayloads.reduce((sum, p) => sum + p.Amount, 0),
                      2,
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {pmsConfig?.default_charge_type && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Charge type: {pmsConfig.default_charge_type}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <SWLoadingButtonOutlined
            onClick={() => setShowUploadConfirm(false)}
            disabled={isUploading}
          >
            Cancel
          </SWLoadingButtonOutlined>
          <SWLoadingButton
            onClick={handleConfirmUpload}
            loading={isUploading}
            disabled={loadingOccupants || occupiedPayloads.length === 0}
          >
            {isUploading
              ? 'Uploading...'
              : `Upload ${occupiedPayloads.length} Charge${
                  occupiedPayloads.length !== 1 ? 's' : ''
                }`}
          </SWLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
